import { useState } from 'react';
import { Path, useForm, type SubmitErrorHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { type OnSubmitHandler } from '../../../../types/forms.typed';
import {
  type BankOfIrelandForm,
  bankofirelandFormSchema,
  type QueryParameters,
  InitialFormValues,
} from '../../queries/schema/carFinance.schema';
import { ResidentialDetails } from './panels/ResidentialDetails/ResidentialDetails';
import { EmploymentDetails } from './panels/EmploymentDetails/EmploymentDetails';
import { PersonalDetails } from '../../components/Panels/PersonalDetails/PersonalDetails';
import { BankingInformation } from './panels/BankingInformation/BankingInformation';
import { DataProtectionAndConsents } from './panels/DataProtectionAndConsents/DataProtectionAndConsents';

import { useSubmitFinanceForm } from '../../queries/hooks/useSubmitFinanceForm';

import { formatDate } from '../formatting';
import { Step1Content } from '../shared/Step1Content';
import {
  clearableRequiredFieldsDefaultValuesBankingDetails,
  clearableRequiredFieldsDefaultValuesCurrentAddress,
  clearableRequiredFieldsDefaultValuesEmploymentDetails,
  clearableRequiredFieldsDefaultValuesPersonalDetails,
  clearableRequiredFieldsDefaultValuesPreviousAddress,
} from '../shared/constants/clearableRequiredDefaultValues';
import { THRESHOLD_TO_SHOW_PREVIOUS_ADDRESS_FIELDS_IN_YEARS_BOI } from './panels/ResidentialDetails/constants';
import { THRESHOLD_TO_SHOW_PREVIOUS_EMPLOYMENT_FIELDS_IN_YEARS } from './panels/EmploymentDetails/constants';

import { typeSafeObjectKeys } from '@dist-marketplace-frontend/utils';
import {
  TOTAL_STEPS,
  STEP_1_FIELDS,
  STEP_2_FIELDS,
  STEP_3_FIELDS,
  STEP_4_FIELDS,
  STEP_5_FIELDS,
  type StepConfig,
} from './BankOfIrelandStepLogic';
import { useProviderFormNavigation } from '../shared/useProviderFormNavigation';
import { ProviderFormShell } from '../shared/ProviderFormShell';
import { scrollToFirstError } from '../shared/scrollToFirstError';

type BankOfIrelandFinanceFormProps = {
  query: QueryParameters;
  onSubmitCallback: () => void;
  onSubmitErrorCallback: (errorFields: Array<string>) => void;
  onStepChange?: (step: number) => void;
  initialFormData: InitialFormValues;
  adId: string;
};

const BankOfIrelandFinanceForm = (props: BankOfIrelandFinanceFormProps) => {
  const {
    query,
    onSubmitCallback,
    onSubmitErrorCallback,
    onStepChange,
    initialFormData,
    adId,
  } = props;

  const { currentStep, goToNextStep, goToPrevStep } = useProviderFormNavigation(
    TOTAL_STEPS,
    onStepChange,
  );
  const [introConsent, setIntroConsent] = useState(false);

  const minimumDeposit = Number(query.minimumDepositValue);
  const carPrice = initialFormData.carDetails.price;

  const methods = useForm<BankOfIrelandForm>({
    resolver: zodResolver(bankofirelandFormSchema),
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    defaultValues: {
      personalDetails: clearableRequiredFieldsDefaultValuesPersonalDetails,
      currentAddress: {
        ...clearableRequiredFieldsDefaultValuesCurrentAddress,
        addressLine3: '',
      },
      previousAddress: clearableRequiredFieldsDefaultValuesPreviousAddress,
      employmentDetails: clearableRequiredFieldsDefaultValuesEmploymentDetails,
      previousEmploymentDetails: {
        status: '',
        previousOccupation: '',
        previousEmployerName: '',
        timeWithPreviousEmployer: {
          years: '',
        },
      },
      bankingDetails: clearableRequiredFieldsDefaultValuesBankingDetails,
      accountDetails: {
        sortCode: '',
        accountNumber: '',
      },
      loanDetails: {
        ...initialFormData.loanDetails,
        tradeInValue: 0,
        cashDeposit: minimumDeposit,
        totalDeposit: minimumDeposit,
        financeAmount: Math.max(carPrice - minimumDeposit, 0),
      },
      consent: {
        dataConsent: false,
        acceptDocuments: false,
      },
      carDetails: initialFormData.carDetails,
      hiddenFields: initialFormData.hiddenFields,
    },
  });

  const {
    handleSubmit,
    setError,
    setFocus,
    trigger,
    formState: { isDirty },
  } = methods;

  const { mutate, isPending } = useSubmitFinanceForm({
    onValidationError: (validationErrors) => {
      for (const error of validationErrors) {
        setError(error.field as Path<BankOfIrelandForm>, {
          message: error.message,
        });
      }
      setFocus(validationErrors[0].field as Path<BankOfIrelandForm>);
      const fieldsWithErrors = validationErrors.map((error) => error.field);
      onSubmitErrorCallback(fieldsWithErrors);
    },
    onSuccess: onSubmitCallback,
  });

  const onSubmit: OnSubmitHandler<typeof methods> = async (values) => {
    const formattedValues = {
      ...values,
      personalDetails: {
        ...values.personalDetails,
        dateOfBirth: formatDate(
          values.personalDetails.dateOfBirth.toLocaleDateString('en-IE'),
        ),
      },
    };

    mutate(formattedValues);
  };

  const onInvalidBankOfIrelandSubmission: SubmitErrorHandler<
    BankOfIrelandForm
  > = async (errors) => {
    const fieldsWithErrors = typeSafeObjectKeys(errors);
    onSubmitErrorCallback(fieldsWithErrors);
  };

  const handleAdvanceStep = async (fields: Path<BankOfIrelandForm>[]) => {
    const isValid = await trigger(fields);
    if (isValid) {
      goToNextStep();
    } else {
      scrollToFirstError();
    }
  };

  const handleSubmitStep = handleSubmit(
    onSubmit,
    onInvalidBankOfIrelandSubmission,
  );

  const steps: StepConfig[] = [
    {
      title: 'Personal details',
      content: <PersonalDetails />,
      onNext: () => handleAdvanceStep(STEP_2_FIELDS),
    },
    {
      title: 'Residential details',
      content: <ResidentialDetails />,
      onNext: async () => {
        const years = methods.getValues(
          'currentAddress.timeAtCurrentAddress.years',
        );
        const showPreviousAddress =
          years !== '' &&
          Number(years) <=
            THRESHOLD_TO_SHOW_PREVIOUS_ADDRESS_FIELDS_IN_YEARS_BOI;

        const isBaseValid = await trigger(STEP_3_FIELDS);

        if (!showPreviousAddress) {
          if (isBaseValid) goToNextStep();
          else scrollToFirstError();
          return;
        }

        // zodResolver does not surface superRefine errors for conditional nested
        // objects when trigger() is called with specific field paths — the
        // cross-field refinement runs at schema root level but its issues are
        // never mapped back to the triggered fields. We validate manually here
        // so the user sees inline errors on empty required previous address fields.
        const prevValues = methods.getValues('previousAddress');
        let hasPrevAddressErrors = false;

        if (!prevValues?.addressLine1) {
          setError('previousAddress.addressLine1', {
            type: 'custom',
            message: 'This field is required',
          });
          hasPrevAddressErrors = true;
        }
        if (!prevValues?.county) {
          setError('previousAddress.county', {
            type: 'custom',
            message: 'This field is required',
          });
          hasPrevAddressErrors = true;
        }

        if (isBaseValid && !hasPrevAddressErrors) {
          goToNextStep();
        } else {
          scrollToFirstError();
        }
      },
    },
    {
      title: 'Employment details',
      content: <EmploymentDetails />,
      onNext: async () => {
        const years = methods.getValues(
          'employmentDetails.timeWithEmployer.years',
        );
        const showPreviousEmployment =
          years !== '' &&
          Number(years) <=
            THRESHOLD_TO_SHOW_PREVIOUS_EMPLOYMENT_FIELDS_IN_YEARS;

        const isBaseValid = await trigger(STEP_4_FIELDS);

        if (!showPreviousEmployment) {
          if (isBaseValid) goToNextStep();
          else scrollToFirstError();
          return;
        }

        // zodResolver does not surface superRefine errors for conditional nested
        // objects when trigger() is called with specific field paths — the
        // cross-field refinement runs at schema root level but its issues are
        // never mapped back to the triggered fields. We validate manually here
        // so the user sees inline errors on empty required previous employment fields.
        const prevEmp = methods.getValues('previousEmploymentDetails');
        let hasPrevEmploymentErrors = false;

        if (!prevEmp?.status) {
          setError('previousEmploymentDetails.status', {
            type: 'custom',
            message: 'This field is required',
          });
          hasPrevEmploymentErrors = true;
        }
        if (!prevEmp?.previousOccupation) {
          setError('previousEmploymentDetails.previousOccupation', {
            type: 'custom',
            message: 'This field is required',
          });
          hasPrevEmploymentErrors = true;
        }
        if (!prevEmp?.previousEmployerName) {
          setError('previousEmploymentDetails.previousEmployerName', {
            type: 'custom',
            message: 'This field is required',
          });
          hasPrevEmploymentErrors = true;
        }
        if (!prevEmp?.timeWithPreviousEmployer?.years) {
          setError('previousEmploymentDetails.timeWithPreviousEmployer.years', {
            type: 'custom',
            message: 'This field is required',
          });
          hasPrevEmploymentErrors = true;
        }

        if (isBaseValid && !hasPrevEmploymentErrors) {
          goToNextStep();
        } else {
          scrollToFirstError();
        }
      },
    },
    {
      title: 'Banking information',
      content: <BankingInformation />,
      onNext: async () => {
        const bankName = methods.getValues('bankingDetails.bankName');
        const showAccountDetails = bankName === 'Bank of Ireland';

        const isBaseValid = await trigger(STEP_5_FIELDS);

        if (!showAccountDetails) {
          if (isBaseValid) goToNextStep();
          else scrollToFirstError();
          return;
        }

        // zodResolver does not surface superRefine errors for conditional nested
        // objects when trigger() is called with specific field paths — the
        // cross-field refinement runs at schema root level but its issues are
        // never mapped back to the triggered fields. We validate manually here
        // so the user sees inline errors on empty required account detail fields.
        const accountValues = methods.getValues('accountDetails');
        let hasAccountDetailsErrors = false;

        if (!accountValues?.sortCode) {
          setError('accountDetails.sortCode', {
            type: 'custom',
            message: 'This field is required',
          });
          hasAccountDetailsErrors = true;
        }
        if (!accountValues?.accountNumber) {
          setError('accountDetails.accountNumber', {
            type: 'custom',
            message: 'This field is required',
          });
          hasAccountDetailsErrors = true;
        }

        if (isBaseValid && !hasAccountDetailsErrors) {
          goToNextStep();
        } else {
          scrollToFirstError();
        }
      },
    },
    {
      title: 'Data protection and consent',
      content: <DataProtectionAndConsents />,
      onNext: handleSubmitStep,
      nextLabel: 'Submit Application',
      isSubmitting: isPending,
    },
  ];

  const step1Content = (
    <Step1Content
      query={query}
      introConsent={introConsent}
      setIntroConsent={setIntroConsent}
      currentStep={currentStep}
      totalSteps={TOTAL_STEPS}
      adId={adId}
      providerName="Bank of Ireland"
      privacyPolicyUrl="https://www.bankofireland.com/privacy/data-protection-notice/"
      privacyPolicyLinkText="privacy policy"
      onContinue={async () => {
        const isValid = await trigger(STEP_1_FIELDS);
        if (isValid) {
          // TODO: replace with partial lead API call
          goToNextStep();
        } else {
          scrollToFirstError();
        }
      }}
    />
  );

  return (
    <ProviderFormShell
      methods={methods}
      minimumDepositValue={query.minimumDepositValue}
      isDirty={isDirty}
      currentStep={currentStep}
      totalSteps={TOTAL_STEPS}
      steps={steps}
      goToPrevStep={goToPrevStep}
      step1Content={step1Content}
    />
  );
};

export { BankOfIrelandFinanceForm };
